import { PROFICIENCY_CATEGORIES, PROFICIENCY_WEIGHTS, type ProficiencyCategory } from '@/constants/proficiencyCategories';
import { 
  Wrench, Zap, Car, Gauge, Thermometer, Shield, Battery, 
  Cog, Eye, Activity, Settings, ClipboardCheck, Disc, Navigation, Sparkles
} from 'lucide-react';

/**
 * CORE-C GAP-007 — hosting is not owning.
 *
 * `confidence_score` and `sample_size` are NULLABLE because CORE-C genuinely
 * does not always possess them. The live scheduling row
 * (`technicians.proficiencies`) is a `Record<string, number>` — category to
 * score and nothing else — so an adapter reading it has no confidence and no
 * sample size to report. The snapshot column `sample_size` is nullable at rest
 * as well.
 *
 * They were previously `number`, and that is exactly why the defect existed:
 * the shape had nowhere to put "unknown", so the adapters invented `0.5` and
 * `0` to satisfy it. A missing confidence is not low confidence; a missing
 * sample size is not zero jobs.
 *
 * `proficiency_score` stays non-nullable: both sources guarantee it, and
 * widening it for symmetry would invite the opposite error.
 */
export interface ProficiencyData {
  category_key: string;
  proficiency_score: number;
  /** null = CORE-C does not possess it. NEVER a stand-in for low confidence. */
  confidence_score: number | null;
  /** null = CORE-C does not possess it. NEVER a stand-in for zero jobs. */
  sample_size: number | null;
  /**
   * Core-100 R5 (CORE-C F/Prov): the value's evidence class (demonstrated / manager_asserted / passport_carried /
   * unrecorded). Absent or null = the class could not be read, and nothing is said about it.
   */
  provenance_class?: 'demonstrated' | 'manager_asserted' | 'passport_carried' | 'unrecorded' | null;
}

export interface MetricsData {
  avg_efficiency_predicted?: number | null;
  avg_efficiency_book?: number | null;
  diagnostic_accuracy?: number | null;
  comeback_rate?: number | null;
  jobs_completed?: number | null;
}

// Category groupings for collapsible sections
export const CATEGORY_GROUPS = {
  'Mechanical Systems': ['Engine Mechanical', 'Engine Performance', 'Transmission', 'Drivetrain', 'Heating Ventilation A/C'],
  'Electrical & Diagnostics': ['Electrical', 'Diagnostics', 'EV / Hybrid High Voltage', 'Starting & Charging', 'ADAS', 'Restraints'],
  'Chassis Systems': ['Steering & Suspension', 'Brakes', 'Tires', 'Alignment', 'Body & Frame'],
  'Maintenance & Inspection': ['Maintenance', 'Inspections'],
  'Detailing': ['Detailing'],
} as const;

// Map category to Lucide icon component
export function getCategoryIcon(category: string) {
  const iconMap: Record<string, typeof Wrench> = {
    'Engine Mechanical': Cog,
    'Engine Performance': Activity,
    'Transmission': Settings,
    'Drivetrain': Cog,
    'Electrical': Zap,
    'Diagnostics': Eye,
    'EV / Hybrid High Voltage': Battery,
    'Starting & Charging': Battery,
    'ADAS': Navigation,
    'Steering & Suspension': Car,
    'Brakes': Disc,
    'Tires': Disc,
    'Alignment': Gauge,
    'Body & Frame': Car,
    'Maintenance': Wrench,
    'Inspections': ClipboardCheck,
    'Heating Ventilation A/C': Thermometer,
    'Restraints': Shield,
    'Detailing': Sparkles,
  };
  return iconMap[category] || Wrench;
}

// Calculate weighted score for radar chart (score × confidence)
/**
 * The confidence-weighted score, or NULL when confidence is unknown.
 *
 * Multiplying by an invented confidence silently halved displayed scores when
 * the adapter fabricated `0.5`. There is no truthful weighted score without a
 * known weight, and substituting the raw score would report a different metric
 * under the same name.
 */
export function getWeightedScore(score: number, confidence: number | null): number | null {
  if (confidence === null) return null;
  return Math.round(score * confidence);
}

// Get verdict badge based on score and confidence
export type CategoryVerdict = 'Strong Fit' | 'Capable' | 'Developing' | 'Avoid';

/**
 * The verdict, or NULL when the metadata this verdict depends on is unknown.
 *
 * The raw-score branch below is authority for KNOWN-limited data
 * (`sampleSize < 3`). An unknown sample size is not known-limited: it does not
 * tell us which branch applies, so no verdict is produced rather than one
 * constructed from a default. Likewise a known-sufficient sample with unknown
 * confidence cannot take the weighted branch.
 */
export function getCategoryVerdict(
  score: number,
  confidence: number | null,
  sampleSize: number | null,
): CategoryVerdict | null {
  // Unknown sample size cannot select a branch.
  if (sampleSize === null) return null;

  // When we have enough data, use weighted scoring
  if (sampleSize >= 3) {
    if (confidence === null) return null;
    const weighted = score * confidence;
    if (weighted >= 75 && confidence >= 0.7) return 'Strong Fit';
    if (weighted >= 55 && confidence >= 0.5) return 'Capable';
    if (weighted >= 35) return 'Developing';
    return 'Avoid';
  }
  
  // With limited data, use raw score to give a preliminary verdict
  if (score >= 85) return 'Strong Fit';
  if (score >= 65) return 'Capable';
  if (score >= 40) return 'Developing';
  return 'Avoid';
}

export function getVerdictColor(verdict: CategoryVerdict): string {
  switch (verdict) {
    case 'Strong Fit': return 'bg-success/20 text-success border-success/30';
    case 'Capable': return 'bg-primary/20 text-primary border-primary/30';
    case 'Developing': return 'bg-warning/20 text-warning border-warning/30';
    case 'Avoid': return 'bg-destructive/20 text-destructive border-destructive/30';
  }
}

// Generate strength/stretch tags from proficiencies
export interface StrengthTag {
  category: string;
  type: 'strength' | 'good' | 'stretch';
  score: number;
}

export function getStrengthStretchTags(proficiencies: ProficiencyData[]): StrengthTag[] {
  const tags: StrengthTag[] = [];
  
  const sorted = [...proficiencies]
    .filter(p => p.sample_size >= 3)
    .sort((a, b) => (b.proficiency_score * b.confidence_score) - (a.proficiency_score * a.confidence_score));
  
  // Top 2 are strengths (green)
  sorted.slice(0, 2).forEach(p => {
    if (p.proficiency_score * p.confidence_score >= 60) {
      tags.push({ category: p.category_key, type: 'strength', score: p.proficiency_score });
    }
  });
  
  // Next 2 are good fits (yellow)
  sorted.slice(2, 4).forEach(p => {
    if (p.proficiency_score * p.confidence_score >= 40) {
      tags.push({ category: p.category_key, type: 'good', score: p.proficiency_score });
    }
  });
  
  // Bottom 2 with low scores are stretch areas (red)
  const bottom = [...proficiencies]
    .filter(p => p.sample_size >= 2 && p.proficiency_score * p.confidence_score < 40)
    .sort((a, b) => (a.proficiency_score * a.confidence_score) - (b.proficiency_score * b.confidence_score))
    .slice(0, 2);
  
  bottom.forEach(p => {
    tags.push({ category: p.category_key, type: 'stretch', score: p.proficiency_score });
  });
  
  return tags.slice(0, 5);
}

// Generate headline from top proficiencies
export function generateHeadline(proficiencies: ProficiencyData[]): string {
  const sorted = [...proficiencies]
    .filter(p => p.sample_size >= 5 && p.confidence_score >= 0.5)
    .sort((a, b) => (b.proficiency_score * b.confidence_score) - (a.proficiency_score * a.confidence_score));
  
  if (sorted.length === 0) return 'General Technician';
  
  const topCategories = sorted.slice(0, 2).map(p => {
    // Shorten some category names
    const shortNames: Record<string, string> = {
      'Steering & Suspension': 'Suspension',
      'Heating Ventilation A/C': 'HVAC',
      'EV / Hybrid High Voltage': 'EV/Hybrid',
      'Engine Mechanical': 'Engine',
      'Engine Performance': 'Performance',
      'Starting & Charging': 'Electrical',
    };
    return shortNames[p.category_key] || p.category_key;
  });
  
  if (topCategories.length === 1) {
    return `${topCategories[0]} Specialist`;
  }
  
  return `${topCategories[0]} & ${topCategories[1]} Expert`;
}

// Calculate overall grade
export type OverallGrade = 'A+' | 'A' | 'B+' | 'B' | 'C' | 'D';

export function calculateOverallGrade(
  metrics: MetricsData | null,
  proficiencies: ProficiencyData[]
): OverallGrade {
  let score = 50; // Base score
  
  // Efficiency contribution (up to 25 points)
  if (metrics?.avg_efficiency_book) {
    const eff = metrics.avg_efficiency_book * 100;
    if (eff >= 110) score += 25;
    else if (eff >= 100) score += 20;
    else if (eff >= 90) score += 15;
    else if (eff >= 80) score += 10;
    else score += 5;
  }
  
  // Comeback rate contribution (up to 15 points, lower is better)
  if (metrics?.comeback_rate !== null && metrics?.comeback_rate !== undefined) {
    const comeback = metrics.comeback_rate * 100;
    if (comeback <= 1) score += 15;
    else if (comeback <= 3) score += 10;
    else if (comeback <= 5) score += 5;
    else score -= 5;
  }
  
  // Proficiency breadth (up to 10 points)
  const strongCategories = proficiencies.filter(
    p => p.proficiency_score >= 70 && p.confidence_score >= 0.6
  ).length;
  score += Math.min(strongCategories * 2, 10);
  
  // Map to grade
  if (score >= 90) return 'A+';
  if (score >= 80) return 'A';
  if (score >= 70) return 'B+';
  if (score >= 60) return 'B';
  if (score >= 50) return 'C';
  return 'D';
}

export function getGradeColor(grade: OverallGrade): string {
  switch (grade) {
    case 'A+':
    case 'A': return 'bg-success text-success-foreground';
    case 'B+':
    case 'B': return 'bg-primary text-primary-foreground';
    case 'C': return 'bg-warning text-warning-foreground';
    case 'D': return 'bg-destructive text-destructive-foreground';
  }
}

// Generate AI summary paragraph
export function generateAISummary(
  metrics: MetricsData | null,
  proficiencies: ProficiencyData[],
  headline: string
): string {
  const parts: string[] = [];
  
  // Efficiency insight
  if (metrics?.avg_efficiency_book) {
    const eff = Math.round(metrics.avg_efficiency_book * 100);
    if (eff >= 105) {
      parts.push(`Consistently efficient, completing jobs ${eff - 100}% faster than book time.`);
    } else if (eff >= 95) {
      parts.push('Performs at or above book time expectations.');
    } else {
      parts.push(`Currently running ${100 - eff}% slower than book time, showing room for improvement.`);
    }
  }
  
  // Quality insight
  if (metrics?.comeback_rate !== null && metrics?.comeback_rate !== undefined) {
    const comeback = metrics.comeback_rate * 100;
    if (comeback <= 2) {
      parts.push('Low rework rate indicates strong workmanship consistency.');
    } else if (comeback <= 5) {
      parts.push('Moderate comeback rate suggests attention to detail.');
    } else {
      parts.push('Elevated comeback rate warrants quality focus.');
    }
  }
  
  // Strength areas
  const strengths = proficiencies
    .filter(p => p.proficiency_score >= 75 && p.confidence_score >= 0.6)
    .slice(0, 2);
  
  if (strengths.length > 0) {
    const areas = strengths.map(s => s.category_key).join(' and ');
    parts.push(`Strong performance in ${areas}.`);
  }
  
  // Development areas
  const developing = proficiencies
    .filter(p => p.proficiency_score < 50 && p.sample_size >= 3)
    .slice(0, 2);
  
  if (developing.length > 0) {
    const areas = developing.map(s => s.category_key).join(' and ');
    parts.push(`Developing skills in ${areas}.`);
  }
  
  // Workflow recommendation
  if (metrics?.jobs_completed && metrics.jobs_completed > 50) {
    parts.push('Ideal for mid- to high-volume workflow.');
  } else if (metrics?.jobs_completed && metrics.jobs_completed > 20) {
    parts.push('Building experience across job types.');
  }
  
  return parts.join(' ') || 'Performance data is being collected to generate insights.';
}

// Format efficiency delta
export function formatEfficiencyDelta(efficiency: number): { text: string; isPositive: boolean } {
  const delta = Math.round((efficiency - 1) * 100);
  if (delta > 0) {
    return { text: `+${delta}% faster than predicted`, isPositive: true };
  } else if (delta < 0) {
    return { text: `${Math.abs(delta)}% slower than predicted`, isPositive: false };
  }
  return { text: 'On target', isPositive: true };
}

// Get skill bar color based on score
export function getSkillBarColor(score: number): string {
  if (score >= 75) return 'bg-success';
  if (score >= 50) return 'bg-primary';
  if (score >= 25) return 'bg-warning';
  return 'bg-destructive';
}
